import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldOff, Trash2 } from "lucide-react";
import { useState } from "react";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { useSession } from "@/hooks/useSession";
import { api, type Application, type Ban } from "@/lib/api";
import { fmtAgo } from "@/lib/utils";

export default function Bans() {
  const qc = useQueryClient();
  const { user } = useSession();
  const canBan = user?.role === "owner" || user?.role === "operator";
  const [open, setOpen] = useState(false);
  const [ip, setIp] = useState("");
  const [service, setService] = useState("");
  const [exp, setExp] = useState("86400");
  const [err, setErr] = useState("");

  const { data: bans } = useQuery({
    queryKey: ["bans"],
    queryFn: () => api.get<{ bans?: Ban[] } | Ban[]>("/api/bans"),
    refetchInterval: 30_000,
  });
  const { data: apps } = useQuery({
    queryKey: ["apps"],
    queryFn: () => api.get<Application[]>("/api/applications"),
  });

  const items: Ban[] = Array.isArray(bans) ? bans : bans?.bans ?? [];

  const ban = useMutation({
    mutationFn: () =>
      api.post("/api/bans", {
        ip, exp: Number(exp), reason: "console",
        ...(service ? { service } : {}),
      }),
    onSuccess: () => {
      setOpen(false); setIp("");
      qc.invalidateQueries({ queryKey: ["bans"] });
    },
    onError: (e) => setErr(e.message),
  });
  const unban = useMutation({
    mutationFn: (b: Ban) =>
      api.post("/api/bans/unban", { ip: b.ip, ...(b.service ? { service: b.service } : {}) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bans"] }),
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="IP Bans"
        description="Active bans live on the edge (BunkerWeb ban plugin). Bans are global by default — scope to a service to narrow blast radius."
      >
        {canBan && (
          <Button size="sm" onClick={() => setOpen(true)}>
            <ShieldOff className="h-4 w-4" /> New ban
          </Button>
        )}
      </PageHeader>

      <Card>
        <Table>
          <THead>
            <TR><TH>IP</TH><TH>Scope</TH><TH>Reason</TH><TH>Created</TH><TH></TH></TR>
          </THead>
          <TBody>
            {items.map((b, i) => (
              <TR key={`${b.ip}-${b.service ?? "global"}-${i}`}>
                <TD className="font-mono text-xs">{b.ip}</TD>
                <TD className="text-xs">
                  {b.service ? <Badge variant="secondary">{b.service}</Badge> : <Badge variant="warning">global</Badge>}
                </TD>
                <TD className="text-xs">{String(b.reason ?? "—")}</TD>
                <TD className="text-xs text-muted-foreground">{b.created_at ? fmtAgo(String(b.created_at)) : "—"}</TD>
                <TD className="text-right">
                  {canBan && (
                    <Button size="sm" variant="ghost" disabled={unban.isPending}
                      onClick={() => unban.mutate(b)} title="Remove ban">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {items.length === 0 && (
          <EmptyState icon={ShieldOff} title="No active bans"
            hint="Bad-behavior auto-bans and manual bans appear here." />
        )}
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} title="Ban an IP"
        description="Global bans apply to every service behind the edge.">
        <form
          className="space-y-4"
          onSubmit={(e) => { e.preventDefault(); setErr(""); ban.mutate(); }}
        >
          <div className="space-y-1.5">
            <Label>IP or CIDR</Label>
            <Input value={ip} onChange={(e) => setIp(e.target.value)}
              placeholder="203.0.113.10" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Scope</Label>
              <Select value={service} onChange={(e) => setService(e.target.value)}>
                <option value="">global (all services)</option>
                {(apps ?? []).map((a) => (
                  <option key={a.id} value={a.bw_service}>{a.name}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Duration</Label>
              <Select value={exp} onChange={(e) => setExp(e.target.value)}>
                <option value="3600">1 hour</option>
                <option value="86400">24 hours</option>
                <option value="604800">7 days</option>
                <option value="0">permanent</option>
              </Select>
            </div>
          </div>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={ban.isPending}>{ban.isPending ? "Banning…" : "Ban"}</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
